import { useMemo } from 'react';
import { useAssetLinks } from '@/pages/ValuationsV2/hooks/useAssetLinks';
import type { Asset } from '@/types/GraphqlSchema';
import { titleize } from '@/utils/helpers';
import type { CompanyParticipation, Metric } from '../types';

const getLatestParticipations = (asset: Asset | null) => {
  if (!asset?.participations || asset.participations.length === 0) {
    return null;
  }

  const latestEffectiveDate = asset.participations[0]?.effectiveDate;
  return asset.participations.filter(
    participation => participation.effectiveDate === latestEffectiveDate && participation.company,
  );
};

const getOperators = (
  asset: Asset | null,
  operatorLinks: ReturnType<typeof useAssetLinks>['operators'],
): CompanyParticipation[] | null => {
  const latestParticipations = getLatestParticipations(asset);
  if (!latestParticipations) {
    return null;
  }

  const operators = latestParticipations
    .filter(participation => participation.isCompanyOperator)
    .map(participation => {
      const companyName = `${participation.company?.name}`;
      const operatorLink = operatorLinks.find(link => link.name === companyName);
      return {
        company: companyName,
        needsIcon: participation.mAndAOpportunity ?? false,
        percentStake: participation.percentStake,
        formattedPercentStake: participation.formattedPercentStake,
        link: operatorLink?.url ?? null,
      };
    });

  return operators.length > 0 ? operators : null;
};

const getPartners = (asset: Asset | null): CompanyParticipation[] | null => {
  const latestParticipations = getLatestParticipations(asset);
  if (!latestParticipations) {
    return null;
  }

  const partners = latestParticipations
    .filter(participation => !participation.isCompanyOperator)
    .sort(
      (a, b) =>
        (b.percentStake ?? 0) - (a.percentStake ?? 0) ||
        (a.company?.name ?? '').localeCompare(b.company?.name ?? ''),
    )
    .map(participation => ({
      company: `${participation.company?.name}`,
      needsIcon: participation.mAndAOpportunity ?? false,
      percentStake: participation.percentStake,
      formattedPercentStake: participation.formattedPercentStake,
    }));

  return partners.length > 0 ? partners : null;
};

export enum Labels {
  Country = 'Country / Area',
  Operator = 'Operator',
  Partners = 'Partners',
  Basin = 'Basin',
  PrimaryHydrocarbon = 'Primary Hydrocarbon',
  HPHT = 'HPHT',
  ShoreStatus = 'Shore Status',
  DevelopmentStatus = 'Development Status',
  DevelopmentType = 'Development Type',
}

const buildKeyInformationMetrics = (
  asset: Asset | null,
  links: ReturnType<typeof useAssetLinks>,
): Metric[] => {
  if (!asset) {
    return [];
  }

  return [
    {
      label: Labels.Country,
      value: asset.country?.displayName ?? null,
      link: links?.country?.url ?? null,
    },
    {
      label: Labels.Operator,
      value: getOperators(asset, links.operators) ?? null,
    },
    {
      label: Labels.Partners,
      value: getPartners(asset) ?? null,
    },
    {
      label: Labels.Basin,
      value: asset.basin?.name ?? null,
    },
    {
      label: Labels.PrimaryHydrocarbon,
      value: asset.hydrocarbonType ? titleize(asset.hydrocarbonType) : null,
    },
    {
      label: Labels.HPHT,
      value: asset.isHpht ? 'Yes' : asset.isHpht === false ? 'No' : null,
    },
    {
      label: Labels.ShoreStatus,
      value: asset.shoreStatus?.name ?? null,
    },
    {
      label: Labels.DevelopmentStatus,
      value: asset.developmentStatus?.name ?? null,
    },
    {
      label: Labels.DevelopmentType,
      value: asset.developmentType?.name ?? null,
    },
  ];
};

export const useKeyInformationMetrics = (asset: Asset | null): Metric[] => {
  const links = useAssetLinks(asset);

  return useMemo(() => buildKeyInformationMetrics(asset, links), [asset, links]);
};
